import { isURL } from 'class-validator';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';

import ConfirmModal from '@/src/components/common/Modal/ConfirmModal/ConfirmModal';
import { showToast } from '@/src/globalFunction';
import { queryClient } from '@/src/lib/react-query/react-query';

import { dashboardMembersKeys, DashboardMembersResponse } from '../../api/getDashboardMembers';
import { useUpdateReportUrl } from '../../api/updateReportUrl';

function validateUrl(urlString: string) {
  return isURL(urlString);
}

type InsertLinkProps = {
  showModal: boolean;
  toggleShowModal: (open: boolean) => void;
  defaultValue?: string;
  onSave?: (data: { name: string; url: string }) => void;
};

export const InsertLink: React.FC<InsertLinkProps> = ({
  showModal,
  toggleShowModal,
  defaultValue = '',
  onSave,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isValidUrl = validateUrl(value);
  const isNameValid = !!name.trim();

  const isValidOrEmpty = isNameValid && (!value || !value.trim() || isValidUrl);
  const updateReportUrlMutation = useUpdateReportUrl();

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    if (!nameTouched) setNameTouched(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleCancel = () => toggleShowModal(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (onSave) {
        await onSave({ name, url: value });
        toggleShowModal(false);
        return;
      }
      await updateReportUrlMutation.mutateAsync({
        projectId: +projectId,
        newUrl: value,
      });

      queryClient.setQueryData(
        dashboardMembersKeys.list(+projectId),
        (oldData: DashboardMembersResponse) =>
          oldData
            ? {
                ...oldData,
                'looker-reports-url': value,
              }
            : oldData
      );

      // Dispatch Redux action to update lookerReportUrl in project reducer (reducer now merges fields)
      dispatch({
        type: 'SET_PROJECT_SELECTED',
        Selected: {
          lookerReportUrl: value,
        },
      });

      showToast('success', 'Changes successfully saved!');
      toggleShowModal(false);
    } catch (err) {
      setIsSubmitting(false);
    }
  };

  const InsertLinkContent = (
    <>
      {/* Row 1: Report Name label */}
      <div style={{ marginBottom: 8 }}>
        <div className="tw-mb-1 tw-font-brandonTextMedium">Report Name</div>
      </div>
      {/* Row 2: Report Name input */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <input
          id="report-name"
          placeholder="Report Name"
          type="text"
          value={name}
          onChange={handleNameChange}
          onBlur={() => setNameTouched(true)}
          className={!isNameValid && nameTouched ? 'error' : ''}
          style={{ width: '100%', height: '24px' }}
        />
        {!isNameValid && nameTouched ? (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              background: '#d32f2f',
              color: 'white',
              borderRadius: '6px',
              padding: '6px 16px',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              whiteSpace: 'nowrap',
              zIndex: 10,
              display: 'inline-block',
              marginLeft: 8,
            }}
          >
            Report name required
          </div>
        ) : null}
      </div>
      {/* Row 3: URL label */}
      <div style={{ marginBottom: 8 }}>
        <div className="tw-mb-1 tw-font-brandonTextMedium">Enter a valid URL</div>
      </div>
      {/* Row 4: URL field */}
      <div
        className="input-container"
        style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
      >
        <input
          id="subtask-url"
          placeholder="http://"
          type="url"
          value={value}
          onChange={handleChange}
          className={value && !isValidOrEmpty ? 'error' : ''}
          style={{ width: '100%', marginRight: value && !isValidUrl ? 8 : 0 }}
        />
        {value && !isValidUrl ? (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              background: '#d32f2f',
              color: 'white',
              borderRadius: '6px',
              padding: '6px 16px',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              whiteSpace: 'nowrap',
              zIndex: 10,
              display: 'inline-block',
              marginLeft: 8,
            }}
          >
            Invalid URL
          </div>
        ) : null}
      </div>
    </>
  );

  return (
    <React.Fragment>
      <ConfirmModal
        id={'insert-link-modal'}
        open={showModal}
        title={'Create and Embed Report'}
        message={InsertLinkContent}
        modalWidth={'548px'}
        cancelBtn={{
          label: 'Cancel',
        }}
        confirmBtn={{
          label: 'Save',
          disabled: isSubmitting || !isValidOrEmpty || value === defaultValue,
        }}
        onClose={handleCancel}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </React.Fragment>
  );
};

export default InsertLink;
